// ============================================================================
// financialAnalytics.js — v1.0 — Passo 39 MC2
// Agregações financeiras com matemática segura (Artigo 61.1 da Constituição RDA)
// PROIBIDO: somar floats puros. OBRIGATÓRIO: Math.round a cada iteração.
// ============================================================================

import { TRANSACTION_STATUS, TRANSACTION_TYPES } from '../config/FinancialSchema';

// ── safeAdd ──────────────────────────────────────────────────────────────────
// Art. 61.1: acumulador imune a erro de ponto flutuante
function safeAdd(acc, val) {
  return Math.round(Number(acc) * 100 + Math.round(Number(val || 0) * 100)) / 100;
}

// ── todayISO ─────────────────────────────────────────────────────────────────
function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

// ── currentMonthPrefix ───────────────────────────────────────────────────────
function currentMonthPrefix() {
  return new Date().toISOString().slice(0, 7); // "YYYY-MM"
}

// ── formatBRL (local) ────────────────────────────────────────────────────────
export function fmtBRL(amount) {
  return Number(amount || 0).toLocaleString('pt-BR', {
    style: 'currency', currency: 'BRL', minimumFractionDigits: 2,
  });
}

// ── calculateTotalPending ─────────────────────────────────────────────────────
// Soma de todas as entradas INCOME com status PENDING (ainda não vencidas).
export function calculateTotalPending(ledger) {
  return (Array.isArray(ledger) ? ledger : []).reduce((acc, tx) => {
    if (tx.type !== TRANSACTION_TYPES.INCOME) return acc;
    if (tx.status !== TRANSACTION_STATUS.PENDING) return acc;
    return safeAdd(acc, tx.amount);
  }, 0);
}

// ── calculateOverdue ──────────────────────────────────────────────────────────
// Soma de entradas INCOME com status OVERDUE (vencidas sem pagamento).
export function calculateOverdue(ledger) {
  const today = todayISO();
  return (Array.isArray(ledger) ? ledger : []).reduce((acc, tx) => {
    if (tx.type !== TRANSACTION_TYPES.INCOME) return acc;
    const isOverdue = tx.status === TRANSACTION_STATUS.OVERDUE
      || (tx.status === TRANSACTION_STATUS.PENDING && tx.dueDate && tx.dueDate < today);
    if (!isOverdue) return acc;
    return safeAdd(acc, tx.amount);
  }, 0);
}

// ── calculateTotalPaidThisMonth ───────────────────────────────────────────────
// Art. 61.2 (Regime de Caixa): soma de INCOME com status PAID
// cujo paymentDate (ou competenceDate) esteja no mês corrente.
export function calculateTotalPaidThisMonth(ledger) {
  const monthPrefix = currentMonthPrefix();
  return (Array.isArray(ledger) ? ledger : []).reduce((acc, tx) => {
    if (tx.type !== TRANSACTION_TYPES.INCOME) return acc;
    if (tx.status !== TRANSACTION_STATUS.PAID) return acc;
    const dateRef = tx.paymentDate || tx.competenceDate || '';
    if (!dateRef.startsWith(monthPrefix)) return acc;
    return safeAdd(acc, tx.amount);
  }, 0);
}

// ── calculateTotalPaidAllTime ─────────────────────────────────────────────────
export function calculateTotalPaidAllTime(ledger) {
  return (Array.isArray(ledger) ? ledger : []).reduce((acc, tx) => {
    if (tx.type !== TRANSACTION_TYPES.INCOME) return acc;
    if (tx.status !== TRANSACTION_STATUS.PAID) return acc;
    return safeAdd(acc, tx.amount);
  }, 0);
}

// ── countByStatus ─────────────────────────────────────────────────────────────
export function countByStatus(ledger, status) {
  return (Array.isArray(ledger) ? ledger : []).filter(
    (tx) => tx.type === TRANSACTION_TYPES.INCOME && tx.status === status
  ).length;
}

// ── getOverdueEntries ─────────────────────────────────────────────────────────
export function getOverdueEntries(ledger) {
  const today = todayISO();
  return (Array.isArray(ledger) ? ledger : []).filter((tx) => {
    if (tx.type !== TRANSACTION_TYPES.INCOME) return false;
    return tx.status === TRANSACTION_STATUS.OVERDUE
      || (tx.status === TRANSACTION_STATUS.PENDING && tx.dueDate && tx.dueDate < today);
  });
}
